import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import type {
    PortfolioBacktest,
    PortfolioBacktestRefreshStatus,
    PortfolioBacktestRefreshConfigurationMode
} from '@einhaus/project314-types';
import Button from '@components/UI/Button';
import Modal from '@components/UI/Modal';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';

export default function PortfolioBacktestRefreshControl({
    backtest,
    status,
    onChanged,
    className
}: {
    backtest: Pick<PortfolioBacktest, 'id' | 'status' | 'refreshAttemptId'>;
    status: PortfolioBacktestRefreshStatus | undefined;
    onChanged: () => void;
    className?: string;
}) {
    const [open, setOpen] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [mode, setMode] = useState<PortfolioBacktestRefreshConfigurationMode>('followPortfolio');
    const save = useTrpcMutation(trpc.user.configurePortfolioBacktestRefresh.mutationOptions({ onSuccess: onChanged }), {
        successMessage: 'Refresh settings saved',
        fallbackErrorMessage: 'Could not save refresh settings'
    });
    const refresh = useTrpcMutation(trpc.user.requestPortfolioBacktestRefresh.mutationOptions({ onSuccess: onChanged }), {
        successMessage: 'Refresh requested. Your current result stays available.',
        fallbackErrorMessage: 'Could not request a refresh'
    });
    if (!status && (backtest.refreshAttemptId || backtest.status !== 'complete')) return null;
    const baselineId = status?.baselineBacktestId ?? backtest.id;
    const busy = save.isPending || refresh.isPending;
    return (
        <>
            <Button
                aria-label={`Refresh settings for baseline ${baselineId}`}
                title={status?.isEnabled ? 'Automatic refresh enabled' : 'Refresh settings'}
                size="sm"
                variant="dark"
                className={className}
                onClick={() => {
                    setEnabled(status?.isEnabled ?? true);
                    setMode(status?.configurationMode ?? 'followPortfolio');
                    setOpen(true);
                }}
            >
                <FaSyncAlt aria-hidden="true" className={`h-3.5 w-3.5 ${status?.isEnabled ? 'text-blue-300' : ''}`} />
            </Button>
            <Modal isOpen={open} onClose={() => setOpen(false)} title="Keep this backtest updated" size="md">
                <div className="space-y-4 whitespace-normal text-sm">
                    <p>
                        Recalculate every 14 days during a quiet weekend window. The original result and the latest successful refresh
                        remain available.
                    </p>
                    {baselineId !== backtest.id && (
                        <p>These settings manage the original baseline #{baselineId} and all of its refreshed results.</p>
                    )}
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={busy} />
                        Refresh automatically every 14 days
                    </label>
                    <fieldset className="space-y-2" disabled={busy || Boolean(status?.activeAttempt)}>
                        <legend className="mb-2 font-medium">Strategy selections</legend>
                        <label className="flex items-start gap-2">
                            <input
                                type="radio"
                                name={`refresh-mode-${backtest.id}`}
                                checked={mode === 'followPortfolio'}
                                onChange={() => setMode('followPortfolio')}
                            />
                            <span>
                                Follow portfolio selections
                                <span className="block text-xs text-zinc-400">
                                    Use the portfolio’s current profiles, option policies, and exits. Recommended for paper/live
                                    comparisons.
                                </span>
                            </span>
                        </label>
                        <label className="flex items-start gap-2">
                            <input
                                type="radio"
                                name={`refresh-mode-${backtest.id}`}
                                checked={mode === 'keepSelections'}
                                onChange={() => setMode('keepSelections')}
                            />
                            <span>
                                Keep backtest selections
                                <span className="block text-xs text-zinc-400">
                                    Keep the selected profiles and policies, using their current definitions. Useful for research variants.
                                </span>
                            </span>
                        </label>
                    </fieldset>
                    <p className="text-xs text-zinc-400">
                        Both keep the included strategies, start date, training cutoff, and backtest capital assumptions. Results lag
                        available data and may wait for other work to finish.
                    </p>
                    {status && (
                        <div className="space-y-2 rounded bg-zinc-800 p-3">
                            <p>
                                {status.activeAttempt
                                    ? 'Refresh in progress'
                                    : status.refreshRequestedDateTime
                                      ? 'Refresh requested; waiting for an available slot'
                                      : status.isEnabled
                                        ? 'Automatic refresh enabled'
                                        : 'Automatic refresh paused'}
                            </p>
                            <p>Latest entry coverage: {status.dataThroughDateTime}</p>
                            <p>
                                Last successful refresh: {status.lastSuccessfulRefreshDateTime ?? 'Not yet'}
                                {status.lastSuccessfulRefreshDateTime ? ' (Eastern)' : ''}
                            </p>
                            <p>
                                Next eligible weekend dispatch: {status.nextEligibleDateTime ?? 'Paused'}
                                {status.nextEligibleDateTime ? ' (Eastern, subject to capacity)' : ''}
                            </p>
                            {status.lastAttemptDateTime && <p>Last attempt: {status.lastAttemptDateTime} (Eastern)</p>}
                            {status.lastErrorMessage && (
                                <p role="status" className="text-amber-300">
                                    {status.lastErrorMessage}
                                </p>
                            )}
                            {status.mixedWorkerReleases && (
                                <p className="text-amber-300">This result includes work from more than one software release.</p>
                            )}
                            <div className="flex flex-wrap gap-3 text-blue-300">
                                <Link to={`/user/portfolioBacktestStats?id=${status.baselineBacktestId}`}>Original baseline</Link>
                                <Link to={`/user/portfolioBacktestStats?id=${status.latestSuccessfulBacktestId}`}>Latest successful</Link>
                                {status.previousSuccessfulBacktestId && (
                                    <Link to={`/user/portfolioBacktestStats?id=${status.previousSuccessfulBacktestId}`}>
                                        Previous successful
                                    </Link>
                                )}
                                {status.activeAttempt?.backtestId && (
                                    <Link to={`/user/portfolioBacktestStats?id=${status.activeAttempt.backtestId}`}>Current attempt</Link>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button
                            disabled={busy}
                            onClick={() => save.mutate({ baselineBacktestId: baselineId, isEnabled: enabled, configurationMode: mode })}
                        >
                            Save settings
                        </Button>
                        {status && (
                            <Button
                                variant="dark"
                                disabled={busy || Boolean(status.activeAttempt) || Boolean(status.refreshRequestedDateTime)}
                                onClick={() => refresh.mutate({ baselineBacktestId: baselineId })}
                            >
                                Refresh now
                            </Button>
                        )}
                    </div>
                    <p className="text-xs text-zinc-500">Refresh requests are checked hourly. Only one refresh runs at a time.</p>
                </div>
            </Modal>
        </>
    );
}
